import axios from 'axios'

/**
 * HTX API Client for handling all HTX exchange API calls
 * Documentation: https://www.htx.com.ph/en-us/opend/newApiPages
 */
class HTXClient {
  constructor() {
    this.baseURL = 'https://api.htx.com'
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('HTX API Error:', error.response?.data || error.message)
        return Promise.reject(error)
      }
    )
  }

  /**
   * Fetch all market tickers from HTX
   * GET /market/tickers
   * @returns {Promise<Array>} Array of ticker data
   */
  async fetchTickers() {
    try {
      const response = await this.client.get('/market/tickers')
      
      if (response.data && response.data.status === 'ok') {
        return {
          success: true,
          data: response.data.data || [],
        }
      }
      
      return {
        success: false,
        error: 'Invalid response format',
        data: [],
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch tickers',
        data: [],
      }
    }
  }

  /**
   * Fetch ticker for a specific symbol
   * GET /market/detail/merged?symbol={symbol}
   * @param {string} symbol - Trading symbol (e.g., 'btcusdt')
   * @returns {Promise<Object>} Ticker data for specific symbol
   */
  async fetchTickerBySymbol(symbol) {
    try {
      const response = await this.client.get(`/market/detail/merged`, {
        params: { symbol: symbol.toLowerCase() }
      })
      
      if (response.data && response.data.status === 'ok') {
        return {
          success: true,
          data: response.data.tick || {},
        }
      }
      
      return {
        success: false,
        error: 'Invalid response format',
        data: {},
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch ticker',
        data: {},
      }
    }
  }

  /**
   * Fetch 24hr statistics for all symbols
   * GET /market/detail
   * @returns {Promise<Array>} Array of 24hr statistics
   */
  async fetch24hrStats() {
    try {
      const response = await this.client.get('/market/detail')
      
      if (response.data && response.data.status === 'ok') {
        return {
          success: true,
          data: response.data.data || [],
        }
      }
      
      return {
        success: false,
        error: 'Invalid response format',
        data: [],
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch 24hr stats',
        data: [],
      }
    }
  }

  /**
   * Transform HTX ticker data to match internal pricing format
   * @param {Object} htxTicker - HTX ticker data
   * @param {string} pairName - Internal pair name format
   * @returns {Object} Transformed pricing data
   */
  transformTickerData(htxTicker, pairName) {
    if (!htxTicker || !htxTicker.symbol) {
      return null
    }

    const {
      symbol,
      open,
      high,
      low,
      close,
      amount, // volume in base currency
      vol,    // volume in quote currency
      bid,    // best bid price
      ask,    // best ask price
    } = htxTicker

    // Calculate percentage change
    const openPrice = parseFloat(open) || 0
    const closePrice = parseFloat(close) || 0
    const percentage = openPrice > 0 ? ((closePrice - openPrice) / openPrice) * 100 : 0

    return {
      symbol: symbol.toUpperCase(),
      pairName: pairName,
      currentPrice: parseFloat(close) || 0,
      open: parseFloat(open) || 0,
      high: parseFloat(high) || 0,
      low: parseFloat(low) || 0,
      volume: parseFloat(amount) || 0,
      volumeQuote: parseFloat(vol) || 0,
      percentage: percentage,
      isPositive: percentage >= 0,
      buyPrice: parseFloat(ask) || 0,
      sellPrice: parseFloat(bid) || 0,
      lastUpdated: Date.now(),
    }
  }

  /**
   * Map internal pair names to HTX symbol format
   * @param {string} pairName - Internal pair name (e.g., 'BTC/USDT', 'BTC-USDT')
   * @returns {string} HTX symbol format (e.g., 'btcusdt')
   */
  mapPairToHTXSymbol(pairName) {
    if (!pairName) return ''
    
    return pairName
      .replace('/', '')
      .replace('-', '')
      .toLowerCase()
  }

  /**
   * Fetch market depth (order book) for a specific symbol
   * GET /market/depth?symbol={symbol}&type=step0
   * @param {string} symbol - Trading symbol (e.g., 'btcusdt')
   * @param {string} type - Depth type (default: 'step0')
   * @returns {Promise<Object>} Order book data
   */
  async fetchMarketDepth(symbol, type = 'step0') {
    try {
      const response = await this.client.get('/market/depth', {
        params: { 
          symbol: symbol.toLowerCase(),
          type: type
        }
      })
      
      if (response.data && response.data.status === 'ok') {
        const tick = response.data.tick || {}
        return {
          success: true,
          data: {
            symbol: symbol.toUpperCase(),
            pairName: symbol.toUpperCase(),
            bids: tick.bids || [],
            asks: tick.asks || [],
            timestamp: tick.ts || Date.now()
          }
        }
      }
      
      return {
        success: false,
        error: 'Invalid response format',
        data: null,
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch market depth',
        data: null,
      }
    }
  }

  /**
   * Get pricing data for multiple pairs
   * @param {Array<string>} pairNames - Array of internal pair names
   * @returns {Promise<Array>} Array of transformed pricing data
   */
  async getPricingForPairs(pairNames) {
    try {
      // Fetch all tickers at once for efficiency
      const tickersResponse = await this.fetchTickers()
      
      if (!tickersResponse.success) {
        return {
          success: false,
          error: tickersResponse.error,
          data: [],
        }
      }

      const tickers = tickersResponse.data
      const pricingData = []

      // Map each internal pair to HTX data
      for (const pairName of pairNames) {
        const htxSymbol = this.mapPairToHTXSymbol(pairName)
        const ticker = tickers.find(t => t.symbol === htxSymbol)
        
        if (ticker) {
          const transformedData = this.transformTickerData(ticker, pairName)
          if (transformedData) {
            pricingData.push(transformedData)
          }
        }
      }

      return {
        success: true,
        data: pricingData,
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to get pricing for pairs',
        data: [],
      }
    }
  }
}

export default HTXClient
